import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import DialogContainer from 'components/common/Dialog/DialogContainer';
import ReferralShareCard from 'components/ReferralShareCard';
import {
    LiffShareButton,
    CopyShareButton,
    OtherShareButton
} from 'client/components/Result/Share/button';
import useScrollElementOnMount from 'client/hooks/useScrollElementOnMount';
import { getDescription } from 'editor/game_mgmShare/utils';

// 參考邀請模組的邀請卡
/**
 * @method DialogReferralShareCard - 分享邀請卡 Dialog
 * @param {Object}   props
 * @param {String}   props.variant                 - 卡片類型 outlined | contained
 * @param {Boolean}  props.open                    - dialog 開關
 * @param {Boolean}  props.isFixedWindow           - 滿版 dialog (撐滿整個螢幕)
 * @param {Function} props.onClose                 - 按鈕 icon 隔壁的文字
 * @param {String}   props.dialogTextColor         - 按鈕 icon 隔壁的文字顏色
 * @param {String}   props.dialogBgColor           - 按鈕主題顏色 (背景顏色)
 * @param {Boolean}  props.enableLineLiff          - 開啟 liff 分享按鈕
 * @param {Object}   props.referralShareCardConfig - 邀請卡設定 (title, description, imageUrl, button )
 * @param {Boolean}  props.isShowImage             - 顯示邀請卡圖片
 * @param {Boolean}  props.isShowReferralCode      - 顯示邀請碼
 * @param {String}   props.referralCode            - 邀請碼
 * @param {String}   props.referralLink            - 邀請碼連結
 * @returns {JSX.Element}
 */
const DialogReferralShareCard = ({
    variant,
    open,
    isFixedWindow,
    onClose,
    dialogTextColor,
    dialogBgColor,
    backdropColor,
    enableLineLiff,
    referralShareCardConfig: { title, description, imageUrl, button },
    isShowImage,
    isShowReferralCode,
    referralCode,
    referralLink,
    isFetchingData
}) => {
    // [860t12ray] 讓 promo 活動在作為 iframe 被嵌入在 page 的 情況下， dialog 打開時，要自動 scroll 到 promo 中間，讓 dialog 置中
    const bindRef = useScrollElementOnMount({ trigger: open });
    return (
        <DialogContainer
            open={open}
            isFixedWindow={isFixedWindow}
            disableBackdrop={false}
            onClose={onClose}
            themeTextColor={dialogTextColor}
            themeBgColor={dialogBgColor}
            backdropColor={backdropColor}
            ref={bindRef}
        >
            <div css={contentContainerStyle}>
                <ReferralShareCard
                    variant={variant}
                    type={enableLineLiff ? 'line' : 'default'}
                    title={title}
                    description={getDescription(
                        enableLineLiff,
                        { invitationDesc: description, invitationTitle: title },
                        isShowReferralCode ? referralCode : '',
                        referralLink
                    )}
                    isShowImage={isShowImage}
                    imageUrl={imageUrl}
                    buttonConfig={button}
                    isShowReferralCode={isShowReferralCode}
                    referralCode={referralCode || '-'}
                />
            </div>
            <div css={footerContainerStyle}>
                {enableLineLiff && (
                    <LiffShareButton
                        url={referralLink}
                        color={dialogTextColor}
                        variant={variant}
                        isFetchingData={isFetchingData}
                    />
                )}
                <CopyShareButton
                    url={getDescription(
                        false,
                        { invitationDesc: description, invitationTitle: title },
                        isShowReferralCode ? referralCode : '',
                        referralLink
                    )}
                    color={dialogTextColor}
                    variant={variant}
                    isFetchingData={isFetchingData}
                />
                <OtherShareButton
                    url={referralLink}
                    title={title}
                    text={getDescription(
                        false,
                        { invitationDesc: description, invitationTitle: title },
                        isShowReferralCode ? referralCode : '',
                        referralLink,
                        true
                    )}
                    color={dialogTextColor}
                    variant={variant}
                    isFetchingData={isFetchingData}
                />
            </div>
        </DialogContainer>
    );
};

export default DialogReferralShareCard;

const contentContainerStyle = css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
`;

const footerContainerStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    margin-bottom: 8px;
    gap: 24px;
`;

DialogReferralShareCard.propTypes = {
    variant: PropTypes.oneOf(['outlined', 'contained']),
    open: PropTypes.bool,
    isFixedWindow: PropTypes.bool,
    onClose: PropTypes.func,
    dialogTextColor: PropTypes.string,
    dialogBgColor: PropTypes.string,
    backdropColor: PropTypes.string,
    enableLineLiff: PropTypes.bool,
    referralShareCardConfig: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        imageUrl: PropTypes.string,
        button: PropTypes.shape({
            enableDisplay: PropTypes.bool,
            label: PropTypes.string,
            uri: PropTypes.string,
            textColor: PropTypes.string,
            bgColor: PropTypes.string
        })
    }),
    isShowImage: PropTypes.bool,
    isShowReferralCode: PropTypes.bool,
    referralCode: PropTypes.string,
    referralLink: PropTypes.string
};
